import {
  Button,
  Flex,
  Grid,
  Label,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import * as Icons from "@mui/icons-material";
import { HexColorPicker } from "react-colorful";

import useErrors from "hooks/useErrors";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import { valueTypes } from "config/constants";
import ActionValueForm from "./ActionValueForm";
import IconList from "components/IconList";
import CustomPopover from "components/utils/CustomPopover";

const Square = Icons.Square;

const ActionForm = ({ onSubmit, type = "create", initialValues }) => {
  const [actionForm, setActionForm] = useState(initialValues);
  const [iconState, setIconState] = useState({
    visible: false,
    selected: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    shortName: "",
    valueType: "",
  });
  const validateErrors = useErrors({
    name: "isRequired",
    shortName: "isRequired",
    valueType: "isRequired",
  });

  const IconComponent = useMemo(
    () => Icons[actionForm.icon || "Bolt"],
    [actionForm]
  );

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(actionForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (actionForm.valueType === valueTypes[1]) {
      if (actionForm.values?.length <= 1) {
        toast.error(strings.values.isRequired);
        return;
      }
      if (actionForm.values?.some((val) => !val.name || !val.value)) {
        toast.error(strings.values.fieldsMissing);
        return;
      }
    }

    if (!hasErrors) onSubmit(actionForm);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setActionForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);

    if (name === "valueType") {
      setActionForm((prevSt) => ({
        ...prevSt,
        values: [],
        errorMsg: "",
        successMsg: "",
        icon: "",
        iconColor: "",
      }));
    }
  };

  const onChangeColor = (iconColor) => {
    setActionForm((prevSt) => ({
      ...prevSt,
      iconColor,
    }));
  };

  const onChangeValues = (values) => {
    setActionForm((prevSt) => ({
      ...prevSt,
      values: values ?? [],
    }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({
      name: "",
      shortName: "",
    });
    setActionForm(initialValues);
  };

  const onSelectIcon = (icon) => {
    setActionForm((prev) => ({ ...prev, icon }));
    setIconState(() => ({
      visible: false,
      selected: "",
    }));
  };

  useEffect(() => {
    setActionForm(initialValues);
  }, [initialValues]);

  return (
    <>
      <Grid rowGap="15px" columnGap="15px" padding="20px">
        <Grid
          columnGap="15px"
          templateColumns={{ base: "1fr", large: "2fr 2fr" }}
        >
          <TextField
            name="name"
            label={<LabelRequired text={strings.fields.name.label} />}
            value={actionForm.name}
            onChange={onChange}
            errorMessage={errors.name}
            hasError={!!errors.name}
          ></TextField>
          <TextField
            name="shortName"
            label={<LabelRequired text={strings.fields.shortName.label} />}
            value={actionForm.shortName}
            onChange={onChange}
            errorMessage={errors.shortName}
            hasError={!!errors.shortName}
          ></TextField>
        </Grid>
        <TextAreaField
          name="description"
          label={strings.fields.description.label}
          value={actionForm.description}
          onChange={onChange}
        ></TextAreaField>
        <SelectField
          name="valueType"
          value={actionForm.valueType}
          label={<LabelRequired text={strings.fields.valueType.label} />}
          onChange={onChange}
          errorMessage={errors?.valueType}
          hasError={!!errors?.valueType}
        >
          <option value="">{globalStrings.selectOption}</option>
          {valueTypes.map((text) => (
            <option value={text}>{globalStrings.valueTypes[text]}</option>
          ))}
        </SelectField>
        {actionForm.valueType === valueTypes[1] && (
          <ActionValueForm
            title={strings.values.title}
            values={actionForm.values ?? []}
            onChange={onChangeValues}
          />
        )}
        {actionForm.valueType && actionForm.valueType !== valueTypes[1] && (
          <>
            <TextAreaField
              name="errorMsg"
              label={strings.fields.errorMsg.label}
              value={actionForm.errorMsg}
              onChange={onChange}
            ></TextAreaField>
            <TextAreaField
              name="successMsg"
              label={strings.fields.successMsg.label}
              value={actionForm.successMsg}
              onChange={onChange}
            ></TextAreaField>
            <Flex>
              <Grid>
                <Label>{strings.fields.icon.label}</Label>
                <Button
                  title={actionForm.icon || "Bolt"}
                  variation="link"
                  colorTheme="overlay"
                  borderColor="gray"
                  size="small"
                  width="100px"
                  style={{ marginTop: "8px" }}
                  onClick={() =>
                    setIconState({
                      visible: true,
                      selected: actionForm.icon || "Bolt",
                    })
                  }
                >
                  <Grid className="px-1" style={{ justifyItems: "center" }}>
                    <IconComponent style={{ fontSize: "30px" }} />
                    <div style={styles.iconName}>{actionForm.icon || "Bolt"}</div>
                  </Grid>
                </Button>
              </Grid>
              <TextField
                name="iconColor"
                label={strings.fields.iconColor.label}
                value={actionForm.iconColor}
                onChange={onChange}
                outerEndComponent={
                  <CustomPopover
                    content={
                      <HexColorPicker
                        style={{ margin: "20px" }}
                        color={actionForm.iconColor ?? ""}
                        onChange={onChangeColor}
                      />
                    }
                  >
                    <Button color={actionForm.iconColor ?? ""}>
                      <Square />
                    </Button>
                  </CustomPopover>
                }
              ></TextField>
            </Flex>
          </>
        )}
        <Flex justifyContent="end">
          <Button
            children={strings.fields.buttons.reset}
            onClick={resetFormValues}
          ></Button>
          <Button
            children={
              type === "create"
                ? strings.fields.buttons.create
                : strings.fields.buttons.save
            }
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          ></Button>
        </Flex>
      </Grid>
      <IconList
        visible={iconState.visible}
        selected={iconState.selected}
        onSelect={onSelectIcon}
        onClose={() =>
          setIconState(() => ({
            visible: false,
            selected: "",
          }))
        }
      />
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    values: {
      title: "Valores",
      fieldsMissing: "Faltan campos por rellenar en la sección de Valores",
      isRequired: "Deben existir al menos 2 valores",
    },
    fields: {
      name: {
        label: "Nombre",
      },
      description: {
        label: "Descripción",
      },
      shortName: {
        label: "Nombre corto",
      },
      valueType: {
        label: "Tipo de valor",
      },
      errorMsg: {
        label: "Mensaje de error",
        placeholder: "Ingrese mensaje de error",
      },
      successMsg: {
        label: "Mensaje de éxito",
        placeholder: "Ingrese mensaje de éxito",
      },
      icon: {
        label: "Icono",
      },
      iconColor: {
        label: "Color de Icono",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
  },
});

const styles = {
  iconName: {
    textOverflow: "ellipsis",
    width: "90px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textAlign: "center",
  },
};

export default ActionForm;
